/* @flow weak */
import * as React from 'react';

import assertions from 'lib/assertions';
import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import Fieldset from 'components/Fieldset';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import PasswordStrengthField from 'components/PasswordStrengthField';
import SettingsContainer from './SettingsContainer';
import ToastActions from 'actions/ToastActions';
import User from 'entities/User';
import UserActions from 'user/UserActions';

type State = {
  passwordScore?: number,
  newPassword?: string,
  currentPassword?: string,
  passwordConfirmationValid: boolean,
};

class UserPasswordForm extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);

    this.form = React.createRef();
    this.state = {
      passwordScore: undefined,
      newPassword: undefined,
      currentPassword: undefined,
      passwordConfirmationValid: false,
    };
  }

  form;

  resetForm = () => {
    if (this.form && this.form.current) {
      console.log(this.form.current);
      this.form.current.reset();
    }
  };

  submit = () => {
    const user: User = CurrentUser.getUser();
    const { passwordScore, newPassword, currentPassword } = this.state;

    if (!newPassword) {
      console.error('UserPasswordForm - tried to submit without a new password');
      return;
    }

    if (user.hasPassword()) {
      UserActions.changePassword(currentPassword, newPassword)
        .then(() => {
          ToastActions.showSuccessToast('Password has been changed!');
          this.resetForm();

          if (passwordScore !== undefined) {
            this.trackScore();
          }
        })
        .catch(ToastActions.showDefaultErrorToast);
    } else {
      UserActions.createPassword(newPassword)
        .then(() => {
          ToastActions.showSuccessToast('Password has been set!');
          this.resetForm();

          if (passwordScore !== undefined) {
            this.trackScore();
          }
        })
        .catch(ToastActions.showDefaultErrorToast);
    }
  };

  handleValidPasswordConfirmation = () => {
    this.setState({ passwordConfirmationValid: true });
  };

  handleInvalidPasswordConfirmation = () => {
    this.setState({ passwordConfirmationValid: false });
  };

  handleCurrentPasswordUpdated = (currentPassword: string) => {
    this.setState({ currentPassword });
  };

  handleNewPasswordUpdated = (newPassword: string) => {
    this.setState({ newPassword });
  };

  handleScoreUpdated = (passwordScore: number) => {
    this.setState({ passwordScore });
  };

  formIsValid = () => {
    const user: User = CurrentUser.getUser();
    const { passwordConfirmationValid, currentPassword, newPassword } = this.state;
    return user.hasPassword()
      ? passwordConfirmationValid && currentPassword && newPassword
      : passwordConfirmationValid && newPassword;
  };

  trackScore = () => {
    const { passwordScore } = this.state;
    EventTracking.track('password_strength', {
      password_score: passwordScore,
    });
  };

  render() {
    const user: User = CurrentUser.getUser();

    return (
      <form
        ref={this.form}
        onSubmit={e => {
          e.preventDefault();
          this.submit();
        }}>
        <Fieldset legend="Password">
          <SettingsContainer>
            {user.hasPassword() && (
              <Columns2>
                <div>
                  <FormElement
                    data-test="current-password-field"
                    label="Current password"
                    onValidChange={e => this.handleCurrentPasswordUpdated(e.target.value)}
                    onInvalidChange={() => this.handleCurrentPasswordUpdated(undefined)}
                    validate={e => {
                      assertions.lengthGreaterThanOrEqualTo(e.target.value, 6);
                    }}
                    element={Input}
                    elementProps={{
                      required: true,
                      type: 'password',
                      className: 'UserPasswordForm-password',
                      name: 'current_password',
                    }}
                  />
                </div>
              </Columns2>
            )}
            <Columns2>
              <div>
                <PasswordStrengthField
                  name="new_password"
                  label="New password"
                  onScoreUpdated={this.handleScoreUpdated}
                  onPasswordUpdated={this.handleNewPasswordUpdated}
                />
              </div>
              <div>
                <FormElement
                  label="Retype password"
                  onValidChange={this.handleValidPasswordConfirmation}
                  onInvalidChange={this.handleInvalidPasswordConfirmation}
                  validate={e => {
                    if (e.target.value !== this.state.newPassword) {
                      throw Error('Must be same as password');
                    }
                    assertions.notEmpty(e.target.value);
                  }}
                  element={Input}
                  elementProps={{
                    required: true,
                    type: 'password',
                    autoComplete: 'new-password',
                    className: 'UserPasswordForm-password',
                    name: 'password_confirmation',
                  }}
                />
              </div>
            </Columns2>
          </SettingsContainer>
          <Button size="small" type="submit" disabled={!this.formIsValid()}>
            Save
          </Button>
        </Fieldset>
      </form>
    );
  }
}

export default UserPasswordForm;
